import { useAuth } from '@kurotetsu/share/context/AuthContext';
import { Menu } from '@linktivity/link-ui';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styles from './sidebar.module.css';
import { canViewMenu } from './sitebarMenuRole';

const Sidebar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  // 権限参照: https://drive.google.com/drive/folders/1tGmKCW-2FmUkOteU6d4I-QSqc98TA80d
  const agencyId = user?.agentId;

  const isKkt = user.isKKT === true;

  // 関連ページ入口（ツアー情報/团体予約詳細）からレストラン予約作成ページに入った場合、
  // 「レストラン・食事予約一覧」メニューのハイライトを抑制し、来源メニューを強制ハイライト
  const searchParams = new URLSearchParams(location.search);
  const isRestaurantCreateFromTour =
    location.pathname === '/restaurant-bookings/create' &&
    searchParams.has('tourInfoId');
  const isRestaurantCreateFromGroupBooking =
    location.pathname === '/restaurant-bookings/create' &&
    searchParams.has('groupBookingNo');
  const suppressRestaurantMenuActive =
    isRestaurantCreateFromTour || isRestaurantCreateFromGroupBooking;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.inner}>
        <Menu className={styles.menu} accordion>
          {/* ダッシュボード */}
          {canViewMenu('dashboard', user) && (
            <Menu.MenuItem
              label={t('layouts.sidebar.dashboard.title')}
              to="/dashboard"
            />
          )}
          {/* 団体管理 */}
          {canViewMenu('groupManager', user) && (
            <Menu.SubMenu label={t('layouts.sidebar.groupManager.title')}>
              {canViewMenu('groupManager_notificationList', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.groupManager.notificationList')}
                  to="/notifications"
                />
              )}
              {canViewMenu('groupManager_bookingSearch', user) && (
                <Menu.MenuItem
                  label={t(
                    isKkt
                      ? 'layouts.sidebar.groupManager.groupBookingSearch'
                      : 'layouts.sidebar.groupManager.bookingSearch'
                  )}
                  to="/booking-search"
                  className={
                    isRestaurantCreateFromGroupBooking
                      ? styles.forceActive
                      : undefined
                  }
                />
              )}
              {canViewMenu('groupManager_periodSelect', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.groupManager.periodSelect')}
                  to="/search-allotments/period-selection"
                />
              )}
              {canViewMenu('groupManager_bulkOperation', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.groupManager.bulkOperation')}
                  to="/bulk-operation"
                />
              )}
              {canViewMenu('groupManager_tourBookingList', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.groupManager.tourBookingList')}
                  to="/tour-bookings"
                  className={
                    isRestaurantCreateFromTour ? styles.forceActive : undefined
                  }
                />
              )}
              {canViewMenu('groupManager_restaurantBookingList', user) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.groupManager.restaurantBookingList'
                  )}
                  to="/restaurant-bookings"
                  className={
                    suppressRestaurantMenuActive
                      ? styles.suppressActive
                      : undefined
                  }
                />
              )}
              {canViewMenu('groupManager_report', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.groupManager.report')}
                  to="/report"
                />
              )}
            </Menu.SubMenu>
          )}

          {/* 団体売上管理 */}
          {canViewMenu('groupSalesManager', user) && (
            <Menu.SubMenu
              label={t('layouts.sidebar.groupSalesManager.title')}
              name="salesManager"
            >
              {canViewMenu('groupSalesManager_salesManager', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.groupSalesManager.salesManager')}
                  to="/sales-managers"
                />
              )}
            </Menu.SubMenu>
          )}

          {/* 運用管理 */}
          {canViewMenu('serviceManager', user) && (
            <Menu.SubMenu
              label={t('layouts.sidebar.serviceManager.title')}
              name="serviceManager"
            >
              {canViewMenu('serviceManager_trainAssignment', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.serviceManager.trainAssignment')}
                  to="/train-assignments"
                />
              )}
              {canViewMenu('serviceManager_trainAssignmentPublic', user) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.serviceManager.trainAssignmentPublic'
                  )}
                  to="/train-assignment-public"
                />
              )}
              {canViewMenu(
                'serviceManager_specialSeatAdjustmentSetting',
                user
              ) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.serviceManager.specialSeatAdjustmentSetting'
                  )}
                  to="/special-seat-adjustments"
                />
              )}
              {canViewMenu('serviceManager_suspension', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.serviceManager.suspension')}
                  to="/suspensions"
                />
              )}
            </Menu.SubMenu>
          )}

          {/* 駅務管理 */}
          {canViewMenu('stationService', user) && (
            <Menu.SubMenu
              label={t('layouts.sidebar.stationService.title')}
              name="stationService"
            >
              {canViewMenu('stationService_seatSearch', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.seatSearch')}
                  to="/station-service/seat-search"
                />
              )}
              {canViewMenu('stationService_bookingSearch', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.bookingSearch')}
                  to="/station-service/booking-search"
                />
              )}
              {canViewMenu('stationService_kuronagiSeatSearch', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.kuronagiSeatSearch')}
                  to="/station-service/kuronagi-seat-search"
                />
              )}
              {canViewMenu('stationService_couponTicket', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.couponTicket')}
                  to="/station-service/coupon-ticket"
                />
              )}
              {canViewMenu('stationService_freePlan', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.freePlan')}
                  to="/station-service/free-plan"
                />
              )}
              {canViewMenu('stationService_unriddenSeatRelease', user) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.stationService.unriddenSeatRelease'
                  )}
                  to="/station-service/unridden-seat-release"
                />
              )}
              {canViewMenu('stationService_report', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.report')}
                  to="/station-service/report"
                />
              )}
              {canViewMenu('stationService_entryExitSummary', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.entryExitSummary')}
                  to="/station-service/entry-exit-summary"
                />
              )}
              {canViewMenu('stationService_gateSettings', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.gateSettings')}
                  to="/station-service/gate-settings"
                />
              )}
              {canViewMenu('stationService_signageSettings', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.signageSettings')}
                  to="/station-service/signage-settings"
                />
              )}
              {canViewMenu('stationService_deviceMaintenance', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationService.deviceMaintenance')}
                  to="/station-service/device-maintenance"
                />
              )}
            </Menu.SubMenu>
          )}

          {/* アカウント管理 */}
          {canViewMenu('accountManager', user) && (
            <Menu.SubMenu
              label={t('layouts.sidebar.accountManager.title')}
              name="accountManager"
            >
              {canViewMenu('accountManager_operatorUserManagement', user) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.accountManager.operatorUserManagement'
                  )}
                  to="/users"
                />
              )}
              {canViewMenu('accountManager_agencyList', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.accountManager.agencyList')}
                  to="/agencies"
                />
              )}
              {canViewMenu('accountManager_agencyInfo', user) && agencyId && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.accountManager.agencyInfo')}
                  to={`/agencies/${agencyId}`}
                />
              )}
              {canViewMenu('accountManager_individualGroupList', user) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.accountManager.individualGroupList'
                  )}
                  to="/individual-groups"
                />
              )}
              {canViewMenu('accountManager_individualGroupManagement', user) &&
                agencyId && (
                  <Menu.MenuItem
                    label={t(
                      'layouts.sidebar.accountManager.individualGroupManagement'
                    )}
                    to={`/individual-groups/${agencyId}`}
                  />
                )}
              {canViewMenu('accountManager_passwordUpdate', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.accountManager.passwordUpdate')}
                  to={isKkt ? '/password/update' : '/change-password'}
                />
              )}
              {/* 初期データ登録用menu、後々なくす予定です */}
              {canViewMenu(
                'accountManager_initialDataBulkRegistration',
                user
              ) && (
                <Menu.MenuItem
                  label={'初期データ一括登録'}
                  to="/agencies/bulk"
                />
              )}
            </Menu.SubMenu>
          )}

          {/* 運行在庫管理 */}
          {canViewMenu('serviceInventoryManager', user) && (
            <Menu.SubMenu
              label={t('layouts.sidebar.serviceInventoryManager.title')}
              name="serviceUnventoryManager"
            >
              {canViewMenu('serviceInventoryManager_trainFormation', user) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.serviceInventoryManager.trainFormation'
                  )}
                  to="/trains"
                />
              )}
              {canViewMenu(
                'serviceInventoryManager_stationStopPatternSetting',
                user
              ) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.serviceInventoryManager.stationStopPatternSetting'
                  )}
                  to="/station-stop-patterns"
                />
              )}
              {canViewMenu('serviceInventoryManager_trainSchedule', user) && (
                <Menu.MenuItem
                  label={t(
                    'layouts.sidebar.serviceInventoryManager.trainSchedule'
                  )}
                  to="/train-schedules"
                />
              )}
              {canViewMenu('serviceInventoryManager_calendar', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.serviceInventoryManager.calendar')}
                  to="/calendars"
                />
              )}
            </Menu.SubMenu>
          )}

          {/* 団体マスタ管理 */}
          {canViewMenu('masterManager', user) && (
            <Menu.SubMenu
              label={t('layouts.sidebar.masterManager.title')}
              name="masterManager"
            >
              {canViewMenu('masterManager_operatorBasicInfo', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.masterManager.operatorBasicInfo')}
                  to="/operator-basic-info"
                />
              )}
              {canViewMenu('masterManager_groupFareSetting', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.masterManager.groupFareSetting')}
                  to="/group-fare-settings"
                />
              )}
              {canViewMenu('masterManager_groupSchedule', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.masterManager.groupSchedule')}
                  to="/group-schedule"
                />
              )}
              {canViewMenu('masterManager_pricingPlans', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.masterManager.pricingPlans')}
                  to="/pricing-plans"
                />
              )}
              {canViewMenu('masterManager_restaurant', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.masterManager.restaurant')}
                  to="/restaurant-settings"
                />
              )}
              {canViewMenu('masterManager_meal', user) && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.masterManager.meal')}
                  to="/meal-settings"
                />
              )}
            </Menu.SubMenu>
          )}
        </Menu>
      </div>
    </aside>
  );
};

export default Sidebar;
